/**
 * Integrytas Group - Base App (MOBILE)
 * Page: UAT.TIMELINE — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/UatTimelinePage.tsx. The desktop
 * screen is a diagnostic reconstruction console (enter entity + record id, replay
 * cell-level deltas, reconstruct point-in-time state); the mobile presentation is
 * a DISTINCT, scannable audit-activity feed over the SAME ledger:
 *   - reads the recent uat_ledger entries directly via apiDb (the same canonical
 *     audit ledger the desktop RPCs read), resolving each actor (user_id) to a
 *     display name and each workspace_id to its name (§17.B4 — never a raw id),
 *   - a facet-filtered card list + bottom-sheet detail that shows the full
 *     context and the field-level before/after delta for the tapped entry.
 * It is READ-ONLY (the audit trail is never mutated), so it imports no write path.
 * It never re-renders the desktop grid; §16.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Plus, Pencil, Trash2, History } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDateTime } from '../../lib/format_date';
import { MobilePageHeader, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobileLeadTone, MobilePillTone } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

// How many most-recent ledger entries to pull — a bounded, mobile-friendly feed
// (RLS still scopes it: admins see all, users see their own — §5, migration §3).
const MAX_ROWS = 200;

interface MobileAuditRow {
  audit_log_id: number;
  log_timestamp: string;
  action_type: string;
  entity_name: string;
  record_id: string;
  user_id: number | null;
  workspace_id: number | null;
  api_route: string | null;
  txid: number | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

type ActionFacet = 'all' | 'INSERT' | 'UPDATE' | 'DELETE';

interface ActionMeta { labelKey: string; label: string; pill: MobilePillTone; lead: MobileLeadTone }

// CHECK-enum-style action labels: localized via t(), never the bare stored literal
// (§17.B11). Each carries the pill + lead-icon-tone the card/detail uses.
const ACTION_META: Record<string, ActionMeta> = {
  INSERT: { labelKey: 'mobile.uat.action.INSERT', label: 'Created', pill: 'ok', lead: 'green' },
  UPDATE: { labelKey: 'mobile.uat.action.UPDATE', label: 'Updated', pill: 'info', lead: 'blue' },
  DELETE: { labelKey: 'mobile.uat.action.DELETE', label: 'Deleted', pill: 'no', lead: 'red' },
};

const FACET_KEYS: ActionFacet[] = ['all', 'INSERT', 'UPDATE', 'DELETE'];

function ActionIcon({ action }: { action: string }) {
  if (action === 'INSERT') return <Plus />;
  if (action === 'UPDATE') return <Pencil />;
  if (action === 'DELETE') return <Trash2 />;
  return <History />;
}

function changedKeys(row: MobileAuditRow): string[] {
  return Array.from(new Set([...Object.keys(row.before ?? {}), ...Object.keys(row.after ?? {})]));
}

function valueText(value: unknown): string {
  if (value === undefined) return '—';
  return JSON.stringify(value);
}

export function MobileUatTimelinePage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const fmtDateTime = useFormatDateTime();
  const [rows, setRows] = useState<MobileAuditRow[]>([]);
  const [userNames, setUserNames] = useState<Record<number, string>>({});
  const [workspaceNames, setWorkspaceNames] = useState<Record<number, string>>({});
  const [filter, setFilter] = useState('');
  const [actionFacet, setActionFacet] = useState<ActionFacet>('all');
  const [selected, setSelected] = useState<MobileAuditRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('uat_ledger')
        .select('audit_log_id, log_timestamp, action_type, entity_name, record_id, user_id, workspace_id, api_route, txid, old_values, new_values')
        .order('audit_log_id', { ascending: false })
        .limit(MAX_ROWS);
      if (e) throw e;

      type RawRow = {
        audit_log_id: number; log_timestamp: string; action_type: string;
        entity_name: string; record_id: string; user_id: number | null;
        workspace_id: number | null; api_route: string | null; txid: number | null;
        old_values: Record<string, unknown> | null; new_values: Record<string, unknown> | null;
      };
      const list: MobileAuditRow[] = ((data ?? []) as RawRow[]).map((r) => ({
        audit_log_id: r.audit_log_id,
        log_timestamp: r.log_timestamp,
        action_type: r.action_type,
        entity_name: r.entity_name,
        record_id: r.record_id,
        user_id: r.user_id,
        workspace_id: r.workspace_id,
        api_route: r.api_route,
        txid: r.txid,
        before: r.old_values,
        after: r.new_values,
      }));
      setRows(list);

      // Resolve actor ids -> display names (§17.B4 — never a raw id reaches render).
      const ids = Array.from(new Set(list.map((r) => r.user_id).filter((x): x is number => x != null)));
      if (ids.length) {
        const { data: us } = await apiDb
          .from('csm_user_master')
          .select('user_id, display_name, email')
          .in('user_id', ids);
        const m: Record<number, string> = {};
        for (const u of (us ?? []) as { user_id: number; display_name: string | null; email: string }[]) {
          m[u.user_id] = u.display_name || u.email || String(u.user_id);
        }
        setUserNames(m);
      } else {
        setUserNames({});
      }

      // Resolve workspace ids -> names.
      const wsIds = Array.from(new Set(list.map((r) => r.workspace_id).filter((x): x is number => x != null)));
      if (wsIds.length) {
        const { data: ws } = await apiDb
          .from('une_workspace')
          .select('une_workspace_id, name')
          .in('une_workspace_id', wsIds);
        const wm: Record<number, string> = {};
        for (const w of (ws ?? []) as { une_workspace_id: number; name: string | null }[]) {
          wm[w.une_workspace_id] = w.name || String(w.une_workspace_id);
        }
        setWorkspaceNames(wm);
      } else {
        setWorkspaceNames({});
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (labels follow it — §8).
  useEffect(() => { void reload(); }, [activeLocale]);

  const actorName = (userId: number | null): string =>
    userId == null ? t('common.none', 'None') : (userNames[userId] ?? String(userId));

  const workspaceName = (wsId: number | null): string =>
    wsId == null ? '—' : (workspaceNames[wsId] ?? String(wsId));

  const actionLabel = (action: string): string => {
    const meta = ACTION_META[action];
    return meta ? t(meta.labelKey, meta.label) : action;
  };

  const actionPill = (action: string): MobilePillTone => ACTION_META[action]?.pill ?? 'info';
  const actionLead = (action: string): MobileLeadTone => ACTION_META[action]?.lead ?? 'slate';

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (actionFacet !== 'all' && r.action_type !== actionFacet) return false;
      if (!q) return true;
      return (
        r.entity_name.toLowerCase().includes(q) ||
        r.record_id.toLowerCase().includes(q) ||
        actorName(r.user_id).toLowerCase().includes(q)
      );
    });
    // actorName depends on userNames; recompute when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filter, actionFacet, userNames]);

  const facetLabel = (key: ActionFacet): string =>
    key === 'all' ? t('mobile.uat.all', 'All') : actionLabel(key);

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.uat.title', 'Audit Timeline')}
        description={t('mobile.uat.description', 'Recent changes across the platform, with the field-level before and after of each entry.')}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.uat.search', 'Search entity, record or actor')}
          aria-label={t('mobile.uat.search', 'Search entity, record or actor')}
        />
      </div>

      <MobileFilterChips
        chips={FACET_KEYS.map((key) => ({ value: key, label: facetLabel(key) }))}
        isActive={(v) => actionFacet === v}
        onSelect={(v) => setActionFacet(v as ActionFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.uat.none', 'No audit entries match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => (
            <MobileCardRow
              key={r.audit_log_id}
              lead={<ActionIcon action={r.action_type} />}
              leadTone={actionLead(r.action_type)}
              title={r.entity_name}
              subtitle={`${t('mobile.uat.record', 'Record {{id}}', { id: r.record_id })} · ${actorName(r.user_id)} · ${fmtDateTime(r.log_timestamp)}`}
              trailing={<MobileStatusPill tone={actionPill(r.action_type)}>{actionLabel(r.action_type)}</MobileStatusPill>}
              chevron
              onClick={() => setSelected(r)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet — read-only forensic view of one ledger entry. */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.uat.detail', 'Audit entry detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><ActionIcon action={selected.action_type} /></span>
              <span>
                <div className="m-nm">{selected.entity_name}</div>
                <div className="m-em">{t('mobile.uat.record', 'Record {{id}}', { id: selected.record_id })}</div>
              </span>
            </div>

            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.uat.action_label', 'Action')}
                value={<MobileStatusPill tone={actionPill(selected.action_type)}>{actionLabel(selected.action_type)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.uat.when', 'When')} value={fmtDateTime(selected.log_timestamp)} />
              <MobileDetailRow label={t('mobile.uat.actor', 'By')} value={actorName(selected.user_id)} />
              <MobileDetailRow label={t('mobile.uat.workspace', 'Workspace')} value={workspaceName(selected.workspace_id)} />
              <MobileDetailRow label={t('mobile.uat.route', 'Route')} value={selected.api_route ?? '—'} />
              <MobileDetailRow label={t('mobile.uat.txid', 'Txid')} value={selected.txid ?? '—'} />
            </MobileContentCard>

            <MobileContentCard>
              {changedKeys(selected).length === 0 ? (
                <div className="m-empty">{t('mobile.uat.no_delta', 'No field-level changes recorded.')}</div>
              ) : (
                changedKeys(selected).map((k) => (
                  <MobileDetailRow
                    key={k}
                    label={k}
                    value={
                      <>
                        <span style={{ color: 'var(--color-danger)' }}>{valueText(selected.before?.[k])}</span>
                        {' → '}
                        <span style={{ color: 'var(--color-primary)' }}>{valueText(selected.after?.[k])}</span>
                      </>
                    }
                  />
                ))
              )}
            </MobileContentCard>

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
